import React from 'react';
import { Link } from 'react-router-dom';
import { Select, Drawer, DatePicker } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import {
    Table,
    Input,
    Button,
    Text,
    ButtonHierarchies,
    EmptyState,
    InputSizes,
} from '@clockout-dev/design-elements';
import styles from './styles/session-events-drawer.module.scss';

const { RangePicker } = DatePicker;

const TYPE_OPTIONS = [
    { value: 'all', label: 'All sessions' },
    { value: 'new', label: 'New' },
    { value: 'returning', label: 'Returning' },
];

const columns = [
    {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        render: (name, row) => (
            <div className={styles.userCell}>
                {row.user_id ? (
                    <Link
                        to={`/users/user?userId=${encodeURIComponent(row.user_id)}`}
                        className={styles.userLink}
                    >
                        {name || 'Unknown user'}
                    </Link>
                ) : (
                    <Text as="span" size="s" className={styles.cellStrong}>{name || 'Unknown user'}</Text>
                )}
                {row.user_id ? <Text as="span" size="xs" className={styles.cellFaint}>{row.user_id}</Text> : null}
            </div>
        ),
    },
    {
        title: 'Session Start',
        dataIndex: 'date',
        key: 'date',
        render: (value) => <Text as="span" size="s" className={styles.cellMuted}>{value}</Text>,
    },
    {
        title: 'Platform',
        dataIndex: 'platform',
        key: 'platform',
        render: (platform) => (
            <span className={styles.platformTag}>{platform || 'Unknown'}</span>
        ),
    },
    {
        title: 'Type',
        dataIndex: 'isNew',
        key: 'isNew',
        render: (isNew) => (
            <span className={`${styles.typeTag} ${isNew ? styles.typeNew : styles.typeReturning}`}>
                {isNew ? 'New' : 'Returning'}
            </span>
        ),
    },
];

/**
 * Presentational drill-down drawer for the Total Sessions KPI — a Mixpanel-style
 * list of the inferred user sessions in the period, with search / platform /
 * type / date filters and CSV (Excel) export. All state and handlers arrive via
 * the `sessions` prop.
 */
function SessionEventsDrawer({ sessions = {} }) {
    const {
        open = false,
        loading = false,
        rows = [],
        count = 0,
        query = '',
        platform = 'all',
        type = 'all',
        dateRange = null,
        platformOptions = [],
        onSearch,
        onPlatformChange,
        onTypeChange,
        onDateRangeChange,
        onExport,
        onClose,
    } = sessions;

    const platformSelectOptions = [
        { value: 'all', label: 'All platforms' },
        ...platformOptions.map((p) => ({ value: p, label: p })),
    ];

    return (
        <Drawer
            open={open}
            onClose={onClose}
            placement="right"
            width={760}
            destroyOnHidden
            rootClassName={styles.drawer}
            title={
                <Text as="span" className={styles.title}>
                    User Sessions
                    {!loading ? <span className={styles.count}>{count}</span> : null}
                </Text>
            }
            extra={
                <Button
                    hierarchy={ButtonHierarchies.SECONDARY}
                    onClick={onExport}
                    disabled={loading || rows.length === 0}
                    icon={<DownloadOutlined />}
                    className={styles.exportBtn}
                >
                    Export to Excel
                </Button>
            }
        >
            <div className={styles.body}>
                <div className={styles.toolbar}>
                    <Input
                        size={InputSizes.SMALL}
                        allowClear
                        placeholder="Search by name or user ID…"
                        value={query}
                        onChange={(e) => onSearch(e.target.value)}
                        classNames={{ root: styles.search }}
                    />
                    <Select
                        value={platform}
                        onChange={onPlatformChange}
                        options={platformSelectOptions}
                        className={styles.filterSelect}
                        aria-label="Filter by platform"
                    />
                    <Select
                        value={type}
                        onChange={onTypeChange}
                        options={TYPE_OPTIONS}
                        className={styles.filterSelect}
                        aria-label="Filter by session type"
                    />
                    <RangePicker
                        value={dateRange}
                        onChange={onDateRangeChange}
                        className={styles.rangePicker}
                        allowClear
                    />
                </div>

                <div className={styles.tableScroll}>
                    <Table
                        columns={columns}
                        dataSource={rows}
                        loading={loading}
                        rowKey="key"
                        pagination={false}
                        emptyState={<EmptyState title="No sessions for this period." />}
                    />
                </div>
            </div>
        </Drawer>
    );
}

export default SessionEventsDrawer;
